import { useState, useEffect } from 'react'
import {
  Globe,
  Bell,
  Shield,
  Mail,
  Smartphone,
  Save,
  Video,
  Calendar,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Settings,
} from 'lucide-react'
import { adminApi } from '../../lib/api/admin'

interface PlatformSettings {
  platformName: string
  supportEmail: string
  timezone: string
  defaultSessionDuration: string
  autoRecording: boolean
  emailNotifications: boolean
  smsNotifications: boolean
  twoFactorAuth: boolean
  retentionDays: string
}

export default function AdminSettings() {
  const [settings, setSettings] = useState<PlatformSettings>({
    platformName: 'Interview Dress Rehearsal',
    supportEmail: 'support@idr.com',
    timezone: 'America/New_York',
    defaultSessionDuration: '60',
    autoRecording: true,
    emailNotifications: true,
    smsNotifications: false,
    twoFactorAuth: true,
    retentionDays: '30',
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await adminApi.getSettings()
        if (data) {
          setSettings((prev) => ({ ...prev, ...data }))
        }
      } catch (err) {
        console.error('Failed to load settings:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchSettings()
  }, [])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target
    const value =
      target instanceof HTMLInputElement && target.type === 'checkbox'
        ? target.checked
        : target.value
    setSettings({ ...settings, [target.name]: value })
    setSaved(false)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const entries = Object.entries(settings).map(([key, value]) => ({
        key,
        value: String(value),
      }))
      await adminApi.updateSettings(entries)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      console.error('Failed to save settings:', err)
      alert(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="loading-screen">Loading settings...</div>
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Platform Settings</h1>
        <p className="page-subtitle">
          Configure system-wide settings and preferences
        </p>
      </div>

      {/* Settings Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: 24,
          marginBottom: 32,
        }}
      >
        {/* General Settings */}
        <div className="card">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 24,
              color: 'var(--primary-navy)',
            }}
          >
            <Globe size={20} />
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>General Settings</h3>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div className="form-group">
              <label className="form-label">Platform Name</label>
              <input
                type="text"
                name="platformName"
                className="form-input"
                value={settings.platformName}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Support Email</label>
              <input
                type="email"
                name="supportEmail"
                className="form-input"
                value={settings.supportEmail}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Timezone</label>
              <select
                name="timezone"
                className="form-input"
                value={settings.timezone}
                onChange={handleChange}
              >
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">
                Default Session Duration (minutes)
              </label>
              <select
                name="defaultSessionDuration"
                className="form-input"
                value={settings.defaultSessionDuration}
                onChange={handleChange}
              >
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">60 minutes</option>
                <option value="90">90 minutes</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="card">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 24,
              color: 'var(--primary-navy)',
            }}
          >
            <Bell size={20} />
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>Notifications</h3>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            {/* Email Notifications Toggle */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 16,
                background: 'var(--accent-light)',
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                }}
              >
                <Mail
                  size={18}
                  style={{ marginTop: 2, color: 'var(--primary-navy)' }}
                />
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                    Email Notifications
                  </h4>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    Send email alerts for sessions and updates
                  </p>
                </div>
              </div>
              <label
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: 48,
                  height: 26,
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  name="emailNotifications"
                  checked={settings.emailNotifications}
                  onChange={handleChange}
                  style={{
                    opacity: 0,
                    width: 0,
                    height: 0,
                    position: 'absolute',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.emailNotifications
                      ? 'var(--primary-navy)'
                      : '#ccc',
                    transition: '0.3s',
                    borderRadius: 26,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      content: '""',
                      height: 20,
                      width: 20,
                      left: settings.emailNotifications ? 25 : 3,
                      bottom: 3,
                      backgroundColor: 'white',
                      transition: '0.3s',
                      borderRadius: '50%',
                    }}
                  />
                </span>
              </label>
            </div>

            {/* SMS Notifications Toggle */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 16,
                background: 'var(--accent-light)',
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                }}
              >
                <Smartphone
                  size={18}
                  style={{ marginTop: 2, color: 'var(--primary-navy)' }}
                />
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                    SMS Notifications
                  </h4>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    Send SMS reminders for upcoming sessions
                  </p>
                </div>
              </div>
              <label
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: 48,
                  height: 26,
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  name="smsNotifications"
                  checked={settings.smsNotifications}
                  onChange={handleChange}
                  style={{
                    opacity: 0,
                    width: 0,
                    height: 0,
                    position: 'absolute',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.smsNotifications
                      ? 'var(--primary-navy)'
                      : '#ccc',
                    transition: '0.3s',
                    borderRadius: 26,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      content: '""',
                      height: 20,
                      width: 20,
                      left: settings.smsNotifications ? 25 : 3,
                      bottom: 3,
                      backgroundColor: 'white',
                      transition: '0.3s',
                      borderRadius: '50%',
                    }}
                  />
                </span>
              </label>
            </div>

            {/* Auto Recording Toggle */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 16,
                background: 'var(--accent-light)',
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                }}
              >
                <Settings
                  size={18}
                  style={{ marginTop: 2, color: 'var(--primary-navy)' }}
                />
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                    Auto Recording
                  </h4>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    Automatically record all interview sessions
                  </p>
                </div>
              </div>
              <label
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: 48,
                  height: 26,
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  name="autoRecording"
                  checked={settings.autoRecording}
                  onChange={handleChange}
                  style={{
                    opacity: 0,
                    width: 0,
                    height: 0,
                    position: 'absolute',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.autoRecording
                      ? 'var(--primary-navy)'
                      : '#ccc',
                    transition: '0.3s',
                    borderRadius: 26,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      content: '""',
                      height: 20,
                      width: 20,
                      left: settings.autoRecording ? 25 : 3,
                      bottom: 3,
                      backgroundColor: 'white',
                      transition: '0.3s',
                      borderRadius: '50%',
                    }}
                  />
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="card">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 24,
              color: 'var(--primary-navy)',
            }}
          >
            <Shield size={20} />
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>Security Settings</h3>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            {/* 2FA Toggle */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 16,
                background: 'var(--accent-light)',
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                }}
              >
                <Shield
                  size={18}
                  style={{ marginTop: 2, color: 'var(--primary-navy)' }}
                />
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
                    Two-Factor Authentication
                  </h4>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    Require 2FA for admin accounts
                  </p>
                </div>
              </div>
              <label
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: 48,
                  height: 26,
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  name="twoFactorAuth"
                  checked={settings.twoFactorAuth}
                  onChange={handleChange}
                  style={{
                    opacity: 0,
                    width: 0,
                    height: 0,
                    position: 'absolute',
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.twoFactorAuth
                      ? 'var(--primary-navy)'
                      : '#ccc',
                    transition: '0.3s',
                    borderRadius: 26,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      content: '""',
                      height: 20,
                      width: 20,
                      left: settings.twoFactorAuth ? 25 : 3,
                      bottom: 3,
                      backgroundColor: 'white',
                      transition: '0.3s',
                      borderRadius: '50%',
                    }}
                  />
                </span>
              </label>
            </div>

            <div className="form-group" style={{ marginTop: 8 }}>
              <label className="form-label">
                Recording Retention Period (days)
              </label>
              <select
                name="retentionDays"
                className="form-input"
                value={settings.retentionDays}
                onChange={handleChange}
              >
                <option value="7">7 days</option>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Integrations */}
      <div className="card" style={{ marginBottom: 32 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 24,
            color: 'var(--primary-navy)',
          }}
        >
          <Globe size={20} />
          <h3 style={{ fontSize: 18, fontWeight: 600 }}>Integrations</h3>
        </div>
        <p
          style={{
            color: 'var(--text-muted)',
            fontSize: 14,
            marginBottom: 20,
          }}
        >
          Monitor the status of connected services and integrations.
        </p>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {/* Zoom */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 16,
              background: 'var(--accent-light)',
              borderRadius: 10,
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(59, 130, 246, 0.1)',
                  color: '#2563eb',
                }}
              >
                <Video size={20} />
              </div>
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>
                  Zoom
                </h4>
                <p
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    margin: 0,
                  }}
                >
                  Video conferencing for interview sessions
                </p>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 500,
                background: 'rgba(16, 185, 129, 0.1)',
                color: '#059669',
              }}
            >
              <CheckCircle size={16} />
              <span>Configured</span>
            </div>
          </div>

          {/* Calendly */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 16,
              background: 'var(--accent-light)',
              borderRadius: 10,
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0, 107, 255, 0.1)',
                  color: '#006bff',
                }}
              >
                <Calendar size={20} />
              </div>
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>
                  Calendly
                </h4>
                <p
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    margin: 0,
                  }}
                >
                  Scheduling and availability management
                </p>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 500,
                background: 'rgba(245, 158, 11, 0.1)',
                color: '#d97706',
              }}
            >
              <AlertCircle size={16} />
              <span>Not Connected</span>
            </div>
          </div>

          {/* Stripe */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 16,
              background: 'var(--accent-light)',
              borderRadius: 10,
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(99, 91, 255, 0.1)',
                  color: '#635bff',
                }}
              >
                <CreditCard size={20} />
              </div>
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>
                  Stripe
                </h4>
                <p
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    margin: 0,
                  }}
                >
                  Payment processing for session fees
                </p>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 500,
                background: 'rgba(245, 158, 11, 0.1)',
                color: '#d97706',
              }}
            >
              <AlertCircle size={16} />
              <span>Not Connected</span>
            </div>
          </div>
        </div>
      </div>

      {/* Save Actions */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 16,
        }}
      >
        <button
          className="btn btn-secondary"
          onClick={() =>
            setSettings({
              platformName: 'Interview Dress Rehearsal',
              supportEmail: 'support@idr.com',
              timezone: 'America/New_York',
              defaultSessionDuration: '60',
              autoRecording: true,
              emailNotifications: true,
              smsNotifications: false,
              twoFactorAuth: true,
              retentionDays: '30',
            })
          }
        >
          Reset to Defaults
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            'Saving...'
          ) : saved ? (
            <>
              <CheckCircle size={18} />
              Saved
            </>
          ) : (
            <>
              <Save size={18} />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  )
}
